/** Strings of the servers, admin, catalogue and reports screens; merged into `ca` by src/i18n/ca.ts. */
export const ca_servers = {
  serversPage: {
    filterAll: 'Tots',
    filterManaged: 'Gestionats',
    filterLinked: 'Enllaçats',
    filterStopped: 'Aturats',
    search: 'Cerca un servidor',
    empty: 'Cap servidor que coincideixi.',
    emptyHint: 'Canvia el filtre o crea’n un de nou.',
    uptime: (text: string) => `en marxa des de fa ${text}`,
    noUptime: 'sense dades d’arrencada',
    uptimeShort: 'Temps en marxa',
    modsCount: (n: number) => (n === 1 ? '1 mod' : `${n} mods`),
    stateRunning: (uptime: string | null) => (uptime ? `En marxa · ${uptime}` : 'En marxa'),
    stateStopped: 'Aturat',
    vanillaLabel: 'Arma 3 sense mods',
    modsTilesLabel: 'Mods del servidor',
    modsMore: (n: number) => (n === 1 ? '1 mod més' : `${n} mods més`),
    origin: {
      platform: 'De la plataforma',
      panel: 'Del panell',
      readOnly: 'només lectura',
    },
    playersChip: (online: number, max: number) => `${online} / ${max}`,
    portChip: (port: number) => `Port ${port}`,
    noMission: 'Sense missió',
    actionsAll: 'Accions per a tots',
    opsMod: 'Mod @ops',
    opsModHint: 'Aquest servidor carrega el mod de la plataforma.',
    addonInstalled: (v: string | null) => `Mod @ops${v ? ` ${v}` : ''} instal·lat`,
    addonMissing: 'Mod @ops no instal·lat',
    addonOutdated: (v: string) => `Mod @ops: cal actualitzar a ${v}`,
    addonInstall: 'Instal·la / actualitza',
    addonInstalling: 'Instal·lant…',
    addonDone: 'Mod @ops instal·lat al panell.',
    flightModel: {
      standard: 'Vol estàndard forçat',
      advanced: 'Vol avançat forçat',
      player: 'A elecció del jugador',
    },
    flightModelHint:
      'El model de vol dels helicòpters que força la configuració d’aquest servidor.',
    flightForceAll: 'Força el vol estàndard a tots els servidors',
    flightForcing: 'Forçant el vol estàndard…',
    flightResultTitle: 'Model de vol dels helicòpters',
    flightResultNote: 'No s’ha reiniciat res: cada servidor el pren a la propera arrencada.',
    flightOutcome: {
      changed: 'canviat a vol estàndard',
      already: 'ja el tenia forçat',
      failed: 'no s’ha pogut canviar',
    },
    panelDown: (why: string | null) => `Panell desconnectat${why ? ` · ${why}` : ''}`,
    open: 'Obre la fitxa',
    switchMission: 'Canvia de missió',
    switchPick: 'Tria una missió publicada…',
    switchHasPlayers: 'Hi ha jugadors al servidor: no es pot canviar la missió ara.',
    switchDone: (folder: string) => `El servidor torna a arrencar amb ${folder}.`,
    scanStarted: (status: string) =>
      status === 'DONE'
        ? 'El contingut dels mods d’aquest servidor ja està llegit.'
        : 'S’està llegint el contingut dels mods d’aquest servidor; segueix-ho al catàleg.',
    scanContent: 'Escaneja el contingut',
    logTail: 'Registre en directe',
    logHide: 'Amaga el registre',
    logRefresh: 'Actualitza',
    logLines: (n: number) =>
      n === 1 ? 'Última línia del registre' : `Últimes ${n} línies del registre`,
    logEmpty: 'El panell no té cap registre d’aquest servidor.',
    modDownload: 'Baixada de mods',
    modHash: 'Empremta de mods',
    restartRequired: 'Cal reiniciar',
    probeCard: 'Servidor de prova',
    setting: {
      title: 'Ambientació',
      intro:
        'Les èpoques en què es pot ambientar una nit en aquest servidor. L’època es tria a cada missió; aquí es diu quines s’ofereixen i quina es proposa primer.',
      suggested: 'Proposta de la plataforma, pendent de confirmar.',
      because: (names: string[]) => `Llegida de: ${names.join(', ')}.`,
      eras: 'Èpoques que s’ofereixen',
      first: 'La que es proposa primer',
      confirm: 'Confirma',
      save: 'Desa',
      proposedFirst: (era: string) => `Es proposa primer: ${era}.`,
      adjust: 'Canvia les èpoques',
    },
    respawn: {
      title: 'Reaparició per defecte',
      intro:
        'El que fa una missió que tria «Per defecte del servidor» quan es juga en aquest servidor. Una missió que ha triat la seva pròpia reaparició no mira això. De sèrie, ningú no reapareix.',
      save: 'Desa',
    },
    sectionBasic: 'Bàsic',
    modsFrom: 'Mods',
    modsKeep: 'Els que ja té',
    modsNone: 'Cap mod',
    modsFromServer: 'Els mateixos que el servidor…',
    modsFromFile: 'D’un preset del Launcher (.html)',
    modsFile: 'Fitxer del Launcher',
    modsFileHint:
      'Només se’n llegeix la llista de mods. Els que el panell encara no tingui es posen a descarregar i caldrà tornar-ho a provar quan acabin.',
    downloadsTitle: 'Descàrrega dels mods al panell',
    downloadState: {
      ready: 'a punt',
      downloading: 'baixant',
      queued: 'a la cua',
      error: 'ha fallat',
      unknown: 'el panell no el coneix',
    } as Record<string, string>,
    downloadsReady: 'Tots els mods ja són al panell: torna a enviar el formulari.',
    modsDownloading: (mods: string[]) =>
      `El panell no tenia ${mods.length === 1 ? 'aquest mod' : 'aquests mods'} i ${mods.length === 1 ? 's’ha posat' : 's’han posat'} a descarregar: ${mods.join(', ')}. Torna-ho a provar quan ${mods.length === 1 ? 'acabi' : 'acabin'}.`,
    sectionPlayers: 'Jugadors i seguretat',
    sectionLifecycle: 'Cicle de vida',
    sectionAdvanced: 'Avançat',
  },
  serverDetail: {
    kicker: (port: number) => `Servidor · port ${port}`,
    notFound: 'Aquest servidor no existeix o ja no és al panell.',
    backToList: 'Torna als servidors',
    headless: 'Clients headless',
    modsTitle: 'Mods',
    modsSearch: 'Cerca un mod',
    modsNone: 'Sense mods actius.',
    modsNoMatch: 'Cap mod que coincideixi.',
    configTitle: 'Configuració',
    nowTitle: 'Ara mateix',
    nowStopped: 'Aturat: no hi ha cap missió en marxa.',
    noMission: 'Cap missió en marxa.',
    configuredMission: 'Missió configurada',
    recentTitle: 'Darreres partides aquí',
    recentNone: 'Encara no s’hi ha jugat cap partida.',
    recentAll: 'Tots els informes',
    recentPlayers: (n: number) => (n === 1 ? '1 jugador' : `${n} jugadors`),
    carriesTitle: 'Què porta',
    dlcCount: (n: number) => `${n} DLC`,
    simplex: 'Simplex',
    terrainsCount: (n: number) => (n === 1 ? '1 terreny' : `${n} terrenys`),
    factionsCount: (n: number) => (n === 1 ? '1 facció' : `${n} faccions`),
    erasCount: (n: number) => (n === 1 ? '1 època' : `${n} èpoques`),
    notScanned:
      'El contingut d’aquests mods encara no s’ha llegit: escaneja’l per saber quins terrenys i quines faccions porta.',
    modsAll: (n: number) => `Tots els mods (${n})`,
    flightModel: 'Model de vol dels helicòpters',
    panelStateTitle: 'Estat del panell',
    version: 'Versió',
    map: 'Mapa',
    description: 'Descripció',
    type: 'Tipus',
    dangerZone: 'Zona perillosa',
    dangerZoneHint: 'Esborrar el servidor l’elimina del panell amb tota la seva configuració.',
    noLifecycle: 'El cicle de vida només s’aplica als servidors gestionats.',
    yes: 'sí',
    no: 'no',
  },
  difficulty: 'Dificultat',
  advancedParameters: 'Paràmetres avançats',
  anonymiseAccount: 'Anonimitza un compte',
  adminPage: {
    openPanel: 'Obre el panell',
    panelStatus: 'Estat del panell',
    panelReportsShortcut: 'Informes',
  },
  reportsPage: {
    colMission: 'Missió',
    colServer: 'Servidor',
    colWhen: 'Quan',
    colDuration: 'Durada',
    colPlayers: 'Jugadors',
    colKills: 'Baixes',
    colActions: 'Accions',
  },
} as const;
